/**
 * THE VIEWPORT BANDS, and the one place their numbers live (PRO-929).
 *
 * WHY THIS FILE EXISTS. Before it, the app carried roughly fifteen viewport
 * media queries at ad-hoc values — 330, 576, 640, 880, 960, 980, 992, 1000,
 * 1100, 1280, 1575 — plus a `MOBILE_BREAKPOINT = 768` local to the shell and
 * exactly one `Grid.useBreakpoint` call site. Nothing agreed with anything,
 * so "below the breakpoint" meant a different width in every file. The
 * responsive work for HRA UAT J6.8 touches the shell, the list toolbar, two
 * detail pages, the create-ticket panel and the row menus; those have to
 * change band together or a page half-adapts, which looks worse than not
 * adapting at all. Same rule the rest of this codebase already follows for
 * `LOADING_DELAY_MS` and `SIDEBAR_ROW_METRICS`: name the number once, never
 * re-inline it.
 *
 * WHY 992 IS THE DESKTOP FLOOR. It is antd's own `lg` token, and antd is what
 * paints most of this app. Picking it means a `<Col lg={6}>` and a
 * `@media (max-width: 991px)` rule change state at exactly the same width, so
 * a layout expressed half in spans and half in CSS cannot disagree with
 * itself — which is the defect that makes responsive work rot. It is also
 * where this repo's existing `min-width: 992px` queries already sit, so the
 * choice follows precedent rather than inventing a sixteenth number.
 *
 * THE CONSEQUENCE, STATED PLAINLY. J6.8 tests 960px, which is BELOW this
 * floor, so a 960px window gets the tablet treatment rather than today's
 * desktop one. That is intended: 960px fails the test today. "Desktop
 * unchanged" in this work means >= 992px unchanged, and every rule written
 * against `belowDesktop` is contained by construction — a
 * `@media (max-width: 991px)` block cannot affect a 1200px viewport, so no
 * test and no reviewer has to prove it cannot.
 *
 * WHY THERE IS A SECOND BAND. A 64px icon rail is a reasonable trade on a
 * 960px tablet and an unreasonable one on a 360px phone, where it costs 18%
 * of the screen. So the handset band exists for the things that must go
 * further than "adapt": the sidebar becoming an overlay drawer, the header
 * shedding its icon cluster, columns going fully single-file. It is antd's
 * `md` token for the same reason 992 is `lg`.
 *
 * VIEWPORT OR CONTAINER? Prefer a container measurement for anything INSIDE
 * the content panel — the panel is not the viewport (the sidebar takes 256px
 * expanded, 64px collapsed), so a 960px viewport can hold a ~700px panel, and
 * a viewport query would call that panel "desktop" and be wrong.
 * `src/storybook/components/TableHeader` is the worked example and already
 * ships that pattern. These bands are for the SHELL, where the viewport is
 * the right question, and for CSS where no container query is available.
 */

/** antd `md`. At and above this the sidebar rail and header cluster fit. */
export const TABLET_MIN_WIDTH = 768

/** antd `lg`. At and above this the app renders exactly as it always has. */
export const DESKTOP_MIN_WIDTH = 992

/** Widest viewport that is still NOT desktop. */
export const NARROW_MAX_WIDTH = DESKTOP_MIN_WIDTH - 1

/** Widest viewport that is still a handset. */
export const HANDSET_MAX_WIDTH = TABLET_MIN_WIDTH - 1

/**
 * Media-query prelude for "narrower than desktop" — the band this work is
 * allowed to change. Interpolate into a styled-component:
 *
 *   ${belowDesktop} { flex-wrap: wrap; }
 */
export const belowDesktop = `@media (max-width: ${NARROW_MAX_WIDTH}px)`

/** Media-query prelude for the handset band (phones, portrait tablets). */
export const belowTablet = `@media (max-width: ${HANDSET_MAX_WIDTH}px)`

/**
 * TOUCH INTERACTION, INSIDE THE NARROW BAND — and the conjunction is the
 * whole point, so there is no unconjoined pointer query to reach for.
 *
 * The honest test for "can this element be hovered" is the pointer, not the
 * width: a 1366px touchscreen laptop hovers poorly and a 900px desktop window
 * hovers fine. But the pointer alone is UNBOUNDED BY WIDTH, and a touchscreen
 * laptop at 1920px matches it — which would change row chrome and swap
 * hover for click at a width this work is required to leave exactly as it is.
 * The >= 992px floor therefore wins over the pointer reading, and every
 * consumer of the pointer axis takes both halves by construction.
 *
 * One combined query rather than a nested pair, so the JS hook
 * (`useIsCoarsePointerBelowDesktop`) and the CSS cannot drift.
 */
export const coarsePointerBelowDesktop = `@media (max-width: ${NARROW_MAX_WIDTH}px) and (hover: none)`

/** Minimum comfortable touch target, px. WCAG 2.2 AA (2.5.8) asks for 24; the
 *  platform guidance this follows (Apple HIG, Material) asks for 44. 40 is
 *  what the existing antd control heights round to without a redesign, and is
 *  the floor the row-action work holds. */
export const TOUCH_TARGET_MIN = 40

/**
 * INTERCOM'S OWN MOBILE THRESHOLD — a third-party constant, not one of this
 * app's bands, which is why it is named apart from `belowTablet` and must not
 * be folded into it. Intercom's widget decides `isMobile` from
 * `innerWidth <= 450 || (innerWidth <= 900 && innerHeight <= 450)` and then
 * discards the vertical/horizontal padding it was sent; this prelude is the
 * width-only half of that test, and exists so a CSS backstop reaches exactly
 * the viewports where the boot setting is thrown away. Above it the boot
 * setting still works and owns placement, so nothing here may widen.
 *
 * ACCEPTED NARROWING: a phone in LANDSCAPE (700x400, 844x390) satisfies
 * Intercom's SECOND clause, so Intercom calls it mobile and discards the
 * padding, while a `max-width: 450px` rule does not reach it — the launcher
 * stays at Intercom's own 20px there. That is a known, accepted limit of the
 * width-only form, not an oversight; do not "fix" it by widening the band.
 */
export const INTERCOM_MOBILE_MAX_WIDTH = 450

/** Media-query prelude for the viewports Intercom treats as mobile by width. */
export const intercomMobileWidth = `@media (max-width: ${INTERCOM_MOBILE_MAX_WIDTH}px)`
